import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebounce } from '@/hooks/use-debounce';
import { adminApiFetch } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { avatarColor } from '@/lib/avatar-color';

const MIN_QUERY = 3;

/** Shared input + dropdown chrome for the contextual header search. */
function SearchShell({
  placeholder,
  value,
  onChange,
  onKeyDown,
  open,
  children,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  open: boolean;
  children: ReactNode;
}) {
  return (
    <div className="relative w-96">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        className="border-slate-600 bg-slate-800 pl-10 text-white placeholder:text-slate-500"
      />
      {open && (
        <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-lg border border-slate-700 bg-slate-800 shadow-lg">
          {children}
        </div>
      )}
    </div>
  );
}

function UserSearchField() {
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 300);
  const navigate = useNavigate();
  const { can } = useAuth();

  const adminStaffQuery = useQuery({
    queryKey: ['adminStaff'],
    queryFn: ({ signal }) => adminApiFetch('listAdminStaff', {}, { signal }),
    enabled: can('roles:view'),
  });
  const adminUids = new Set((adminStaffQuery.data ?? []).map((member) => member.uid));

  const trimmed = debounced.trim();
  const open = trimmed.length >= MIN_QUERY;

  const searchQuery = useQuery({
    queryKey: ['userSearch', trimmed],
    queryFn: ({ signal }) => adminApiFetch('searchUsers', { query: trimmed }, { signal }),
    enabled: open,
  });
  const results = searchQuery.data ?? [];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const t = search.trim();
    if (e.key === 'Enter' && t && !t.includes('@') && !/\s/.test(t)) {
      setSearch('');
      navigate(`/users/${t}`);
    }
  };

  return (
    <SearchShell
      placeholder="Search by name, email, or user ID..."
      value={search}
      onChange={setSearch}
      onKeyDown={handleKeyDown}
      open={open}
    >
      {searchQuery.isLoading ? (
        <div className="space-y-3 p-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full bg-slate-700" />
          ))}
        </div>
      ) : results.length === 0 ? (
        <div className="p-4 text-sm text-slate-400">No users found</div>
      ) : (
        results.map((user) => (
          <Link
            key={user.userId}
            to={`/users/${user.userId}`}
            onClick={() => setSearch('')}
            className="flex items-center gap-3 border-b border-slate-700 px-4 py-3 transition last:border-0 hover:bg-slate-700"
          >
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full ${avatarColor(user.userId)} text-xs font-medium text-white`}
            >
              {(user.displayName || '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-white">{user.displayName}</div>
              <div className="truncate text-xs text-slate-400">{user.email}</div>
            </div>
            {adminUids.has(user.userId) && (
              <Badge className="bg-purple-900 text-purple-300 hover:bg-purple-900">Admin</Badge>
            )}
            <span className="font-mono text-xs text-slate-500">{user.userId}</span>
          </Link>
        ))
      )}
    </SearchShell>
  );
}

function OrgSearchField() {
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 300);
  const navigate = useNavigate();

  const trimmed = debounced.trim();
  const open = trimmed.length >= MIN_QUERY;

  const searchQuery = useQuery({
    queryKey: ['orgSearch', trimmed],
    queryFn: ({ signal }) => adminApiFetch('searchOrgsByName', { prefix: trimmed }, { signal }),
    enabled: open,
  });
  const results = searchQuery.data ?? [];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const t = search.trim();
    if (e.key === 'Enter' && t.startsWith('o-')) {
      setSearch('');
      navigate(`/organizations/${t}`);
    }
  };

  return (
    <SearchShell
      placeholder="Search by org name or org ID..."
      value={search}
      onChange={setSearch}
      onKeyDown={handleKeyDown}
      open={open}
    >
      {searchQuery.isLoading ? (
        <div className="p-4 text-sm text-slate-400">Searching...</div>
      ) : results.length === 0 ? (
        <div className="p-4 text-sm text-slate-400">No results found</div>
      ) : (
        results.map((org) => (
          <Link
            key={org.orgId}
            to={`/organizations/${org.orgId}`}
            onClick={() => setSearch('')}
            className="flex items-center gap-3 border-b border-slate-700 px-4 py-3 transition last:border-0 hover:bg-slate-700"
          >
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-lg ${avatarColor(org.orgId)} text-xs font-semibold text-white`}
            >
              {(org.name || '?')[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-white">
                <span className="font-medium text-amber-500">{org.name.slice(0, trimmed.length)}</span>
                {org.name.slice(trimmed.length)}
              </span>
            </div>
            <span className="font-mono text-xs text-slate-500">{org.orgId}</span>
          </Link>
        ))
      )}
    </SearchShell>
  );
}

/**
 * Contextual search that lives in the app header so it is available on a section's list page AND all
 * its sub-pages (detail, sub-detail). Renders the user search under `/users`, the org search under
 * `/organizations`, and nothing elsewhere. Route access is already capability-gated upstream.
 */
export function HeaderSearch() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/users')) return <UserSearchField />;
  if (pathname.startsWith('/organizations')) return <OrgSearchField />;
  return null;
}
